import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, Sparkles } from 'lucide-react';
import api from '@/lib/api';
import Loader from '@/components/common/Loader';
import ProductCard from '@/components/common/ProductCard';
import ProductRail from '@/components/common/ProductRail';
import Reveal from '@/components/common/Reveal';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useStore } from '@/context/StoreContext';
import { inr } from '@/lib/format';
import { toast } from 'sonner';
import { ShoppingBag } from 'lucide-react';

export default function RoomLanding() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [adminBundle, setAdminBundle] = useState(null);
  const { recent, addToCart } = useStore();
  const [recentProducts, setRecentProducts] = useState([]);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0); setData(null); setAdminBundle(null);
    api.get(`/rooms/${slug}`).then((r) => setData(r.data)).catch(() => setData(false));
    api.get(`/rooms/${slug}/bundle`).then((r) => setAdminBundle(r.data)).catch(() => setAdminBundle(null));
  }, [slug]);
  useEffect(() => { if (recent.length) api.post('/products/by-ids', { ids: recent }).then((r) => setRecentProducts(r.data)).catch(() => {}); }, [recent]);

  if (data === null) return <Loader />;
  if (data === false) return <div className="container-gaze py-20 text-center">Room not found</div>;
  const { room, hero_image, products = [], room_sets = [], inspiration = [] } = data;

  const faqs = [
    { q: `How do I style my ${room.name}?`, a: 'Layer textures and heights, anchor with a statement piece, and keep a cohesive palette for a curated, gallery-like finish.' },
    { q: 'Do you offer Cash on Delivery?', a: 'Yes, COD is available on eligible products across India.' },
    { q: 'What is the delivery time?', a: 'Most orders are delivered within 3-7 business days with free shipping over ₹1,999.' },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="room-landing-hero">
        <div className="absolute inset-0"><img src={hero_image} alt={room.name} className="h-full w-full object-cover" /><div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/20" /></div>
        <div className="container-gaze relative flex min-h-[52vh] flex-col justify-center py-16 text-white">
          <nav className="mb-3 flex items-center gap-1 text-xs text-white/70"><Link to="/rooms">Rooms</Link><ChevronRight size={12} /><span>{room.name}</span></nav>
          <p className="eyebrow mb-3 !text-[hsl(var(--gaze-gold))]">Shop by Room</p>
          <h1 className="font-display text-4xl sm:text-6xl">{room.name}</h1>
          <p className="mt-3 max-w-lg text-white/80">{room.description}</p>
        </div>
      </section>

      {/* Curated collections */}
      <section className="container-gaze py-10">
        <Reveal className="mb-5"><p className="eyebrow mb-2">Curated Collections</p><h2 className="text-2xl font-semibold sm:text-3xl">Shop the {room.name}</h2></Reveal>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {['Luxury Collection', 'New Arrivals', "Editor's Picks"].map((c, i) => (
            <Link key={c} to={`/collection/${c.toLowerCase().replace(/'/g, '').replace(/\s+/g, '-')}`} className="group relative overflow-hidden rounded-2xl">
              <img src={inspiration[i] || hero_image} alt={c} className="aspect-[4/3] w-full object-cover transition group-hover:scale-105" />
              <div className="absolute inset-0 bg-black/30" /><span className="absolute bottom-3 left-4 font-display text-lg text-white">{c}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Complete room set - one tap add all. Prefer admin-defined bundle when available. */}
      {adminBundle ? (
        <section className="container-gaze py-8" data-testid="room-complete-set">
          <div className="overflow-hidden rounded-3xl border border-[hsl(var(--gaze-gold))]/40 bg-white">
            <div className="grid md:grid-cols-2">
              <div className="grid grid-cols-2 gap-1 p-1">
                {(adminBundle.products || []).slice(0, 4).map((p) => (
                  <div key={p.id} className="aspect-square overflow-hidden rounded-xl bg-[hsl(var(--gaze-ivory))]"><img src={p.images?.[0]} alt={p.name} className="h-full w-full object-cover" /></div>
                ))}
              </div>
              <div className="flex flex-col justify-center p-8">
                <p className="eyebrow mb-2 !text-[hsl(var(--gaze-gold))]">Complete Room Set</p>
                <h2 className="font-display text-3xl">{adminBundle.name}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{adminBundle.short_description || adminBundle.description}</p>
                <div className="mt-4 flex items-baseline gap-3">
                  <span className="text-2xl font-bold">{inr(adminBundle.bundle_price)}</span>
                  {adminBundle.discount > 0 && <span className="text-sm text-muted-foreground line-through">{inr(adminBundle.subtotal)}</span>}
                  {adminBundle.discount > 0 && <span className="rounded-full bg-[hsl(var(--gaze-ivory))] px-2 py-0.5 text-xs font-semibold text-[hsl(var(--primary))]">Save {inr(adminBundle.discount)}</span>}
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setAdding(true);
                      const items = (adminBundle.products || []).filter((p) => p.in_stock);
                      if (!items.length) { toast.error('This bundle is currently out of stock'); setAdding(false); return; }
                      items.forEach((p) => addToCart(p, 1));
                      toast.success(`Added ${items.length} pieces from ${adminBundle.name}`);
                      setTimeout(() => setAdding(false), 500);
                    }}
                    disabled={adding || !adminBundle.in_stock}
                    data-testid="room-add-all-button"
                    className="inline-flex w-fit items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-7 py-3.5 text-sm font-semibold text-white transition hover:bg-[hsl(var(--gaze-green-bottle))] disabled:opacity-60">
                    <ShoppingBag size={17} /> {adding ? 'Adding...' : 'Add Entire Room to Cart'}
                  </button>
                  <Link to={`/bundle/${adminBundle.slug}`} className="inline-flex w-fit items-center gap-2 rounded-full border border-[hsl(var(--gaze-gold))] bg-white px-6 py-3.5 text-sm font-semibold text-[hsl(var(--primary))]">
                    View the set
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : room_sets.length > 0 && (() => {
        const setTotal = room_sets.reduce((s, p) => s + (p.in_stock ? p.price : 0), 0);
        const bundlePrice = Math.round(setTotal * 0.95);
        const addAll = () => {
          setAdding(true);
          const items = room_sets.filter((p) => p.in_stock);
          if (items.length === 0) { toast.error('These pieces are currently out of stock'); setAdding(false); return; }
          items.forEach((p) => addToCart(p, 1));
          toast.success(`Added ${items.length} pieces from the ${room.name} set`);
          setTimeout(() => setAdding(false), 600);
        };
        return (
          <section className="container-gaze py-8" data-testid="room-complete-set">
            <div className="overflow-hidden rounded-3xl border border-[hsl(var(--gaze-gold))]/40 bg-white">
              <div className="grid md:grid-cols-2">
                <div className="grid grid-cols-2 gap-1 p-1">
                  {room_sets.slice(0, 4).map((p) => (
                    <div key={p.id} className="aspect-square overflow-hidden rounded-xl bg-[hsl(var(--gaze-ivory))]"><img src={p.images?.[0]} alt={p.name} className="h-full w-full object-cover" /></div>
                  ))}
                </div>
                <div className="flex flex-col justify-center p-8">
                  <p className="eyebrow mb-2 !text-[hsl(var(--gaze-gold))]">Complete Room Set</p>
                  <h2 className="font-display text-3xl">The {room.name} Collection</h2>
                  <p className="mt-2 text-sm text-muted-foreground">{room_sets.length} curated pieces styled to work beautifully together. Add the entire look in a single tap and save 5%.</p>
                  <div className="mt-4 flex items-baseline gap-3">
                    <span className="text-2xl font-bold">{inr(bundlePrice)}</span>
                    <span className="text-sm text-muted-foreground line-through">{inr(setTotal)}</span>
                    <span className="rounded-full bg-[hsl(var(--gaze-ivory))] px-2 py-0.5 text-xs font-semibold text-[hsl(var(--primary))]">Save 5%</span>
                  </div>
                  <button onClick={addAll} disabled={adding} data-testid="room-add-all-button" className="mt-5 inline-flex w-fit items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-7 py-3.5 text-sm font-semibold text-white transition hover:bg-[hsl(var(--gaze-green-bottle))] disabled:opacity-60">
                    <ShoppingBag size={17} /> {adding ? 'Adding...' : 'Add Entire Room to Cart'}
                  </button>
                </div>
              </div>
            </div>
          </section>
        );
      })()}
      {room_sets.length > 0 && <ProductRail testId="room-sets" eyebrow="In this set" title="Pieces in the collection" products={room_sets} />}

      {/* Products */}
      <section className="container-gaze py-6">
        <div className="mb-5 flex items-end justify-between"><h2 className="text-2xl font-semibold">Featured for {room.name}</h2><Link to="/shop" className="flex items-center gap-1 text-sm font-semibold text-[hsl(var(--primary))]">View all <ArrowRight size={15} /></Link></div>
        {products.length ? <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">{products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div> : <p className="text-sm text-muted-foreground">New pieces arriving soon.</p>}
      </section>

      {/* Inspiration */}
      <section className="container-gaze py-8" data-testid="room-landing-shop-the-look">
        <Reveal className="mb-5"><p className="eyebrow mb-2">Room Inspiration</p><h2 className="text-2xl font-semibold sm:text-3xl">Get the look</h2></Reveal>
        <div className="columns-2 gap-4 sm:columns-3 [column-fill:_balance]">
          {inspiration.map((img, i) => <img key={i} src={img} alt="Inspiration" loading="lazy" className="mb-4 w-full break-inside-avoid rounded-2xl" />)}
        </div>
      </section>

      {/* AI reco placeholder */}
      <section className="container-gaze py-4">
        <div className="flex items-center gap-4 rounded-2xl border border-[hsl(var(--gaze-gold))]/40 bg-[hsl(var(--gaze-ivory))] p-6">
          <div className="grid h-12 w-12 place-items-center rounded-full bg-white"><Sparkles size={22} className="gaze-gold" /></div>
          <div><p className="text-sm font-semibold">AI-Powered Recommendations</p><p className="text-xs text-muted-foreground">Personalised {room.name} picks are coming soon, tailored to your style.</p></div>
        </div>
      </section>

      {recentProducts.length > 0 && <ProductRail testId="room-recent" eyebrow="Continue Browsing" title="Recently viewed" products={recentProducts} />}

      {/* FAQ */}
      <section className="container-gaze py-10">
        <h2 className="mb-4 text-2xl font-semibold">{room.name} FAQs</h2>
        <Accordion type="single" collapsible className="mx-auto max-w-3xl">
          {faqs.map((f, i) => <AccordionItem key={i} value={`f${i}`}><AccordionTrigger>{f.q}</AccordionTrigger><AccordionContent><p className="text-sm text-foreground/80">{f.a}</p></AccordionContent></AccordionItem>)}
        </Accordion>
      </section>
    </div>
  );
}
